// Small, vanilla interactions. No jQuery, no libraries.

// ---------- Image error fallback ----------
// Wired via data attributes (data-fail-*) instead of inline `onerror=` so the
// CSP can drop `'unsafe-inline'` on script-src. Two patterns supported:
//   data-fail-parent-selector="..."     (closest match)
//   data-fail-add-parent-class="..."   (class to add on the parent)
//   data-fail-self-display="none"      (optional self display style)
(function () {
  document.addEventListener("error", function (e) {
    var img = e.target;
    if (!img || img.tagName !== "IMG") return;
    var sel = img.getAttribute("data-fail-parent-selector");
    var addClass = img.getAttribute("data-fail-add-parent-class");
    var selfDisplay = img.getAttribute("data-fail-self-display");
    if (addClass) {
      var parent = sel ? img.closest(sel) : img.parentElement;
      if (parent) parent.classList.add(addClass);
    }
    if (selfDisplay) img.style.display = selfDisplay;
  }, true); // capture phase, since `error` doesn't bubble
})();

// ---------- Lenis smooth-scroll ----------
// Hand the wheel/touch off to Lenis so the whole site scrolls with one
// consistent easing curve instead of native step-scroll. The library is
// loaded as a deferred CDN script before main.js, so by the time this
// runs `window.Lenis` is on the global. Lerp 0.08 = smooth-but-responsive
// (lower = even smoother but feels laggy; higher = closer to native feel).
// `prefers-reduced-motion` users skip the smoothing entirely.
// Wave 72 / 75 — defer Lenis init AND skip it on touch-first devices.
// Lenis adds ~10KB JS + a permanent RAF loop. On mobile/tablet the smoothing
// is barely perceptible (touch scroll is already inertia-driven by the OS)
// but Lighthouse still counts every raf tick against TBT. Skip on
// (max-width: 1024px) AND on coarse pointers (any phone/tablet/touch laptop).
(function () {
  function start() {
    if (typeof window.Lenis !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(max-width: 1024px), (pointer: coarse)").matches) return;
    var lenis = new Lenis({
      lerp: 0.08,
      smoothWheel: true,
      smoothTouch: false,
      wheelMultiplier: 1,
      touchMultiplier: 1.5
    });
    window.__hoaLenis = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 1500 });
  } else {
    requestAnimationFrame(function () { requestAnimationFrame(start); });
  }
})();

// ---------- Scroll guard (belt + braces) ----------
// The primary guard is an inline <script> in <head> that sets scrollRestoration
// to manual before stylesheets load. Here we just keep snapping to top through
// DOMContentLoaded / load / intro-end so nothing can drift.
if ("scrollRestoration" in history) history.scrollRestoration = "manual";
const snapTop = () => window.scrollTo(0, 0);
snapTop();
document.addEventListener("DOMContentLoaded", snapTop, { once: true });
window.addEventListener("load", snapTop, { once: true });

// ---------- Preloader: clean sequence — logo cycle THEN page enters ----------
// Logo:   one continuous CSS keyframe cycle (0-572ms in / 572-1768ms hold /
//         1768-2800ms out). Delay 200ms → logo is fully gone at ~2800ms.
// Exit:   MIN_VISIBLE waits for logo to finish, THEN JS adds .is-done which
//         triggers panel fade + page-frame slide up. No overlap with logo
//         — the user sees: black → logo in/hold/out → black → site rises.
// Total:  ~4.2s end-to-end.
(() => {
  const body = document.body;
  const preloader = document.querySelector("[data-preloader]");
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.scrollTo(0, 0);
    if (preloader) preloader.classList.add("is-done");
    body.classList.add("is-intro-running");
    body.classList.remove("is-preloading");

    // Force-reveal anything in the initial viewport so hero text doesn't sit
    // in its "hidden" state (opacity 0 + blur) after the preloader clears.
    const vh = window.innerHeight;
    document.querySelectorAll(".split-words, .reveal, .reveal-soft").forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < vh * 0.9 && rect.bottom > 0) {
        el.classList.add("is-visible");
      }
    });

    setTimeout(() => {
      if (preloader) preloader.remove();
      body.classList.remove("is-intro-running");
    }, 260);
  };

  // Wave 83.12 — phone short-circuit re-added (does NOT change preloader UX).
  //   Mobile already has `.preloader { display:none !important }` in CSS,
  //   so the wordmark animation never paints on phones. Waiting 2350ms
  //   for an invisible curtain was holding the hero <h1> at opacity:0 and
  //   tanking mobile LCP. We finish() on next frame for phones only;
  //   desktop keeps the full sequence exactly as the user accepted it.
  // Wave 83.17 — preloader runs on every viewport but mobile uses a
  // condensed timeline so SI doesn't take a 2.3s hit.
  //   • reduced-motion → instant finish.
  //   • ≤768px → 800ms intro (matches the shortened CSS keyframes below).
  //   • desktop → full 2350ms wordmark sequence as before.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    requestAnimationFrame(finish);
    return;
  }
  // Wave 83.28 — mobile preloader cut from 800 → 420ms (CSS animation
  // OUT now ends ~400ms + 120ms panel fade). Brand effect preserved
  // (wordmark IN → OUT visible) but tight enough to free LCP for
  // sub-2.5s mobile scores. Desktop unchanged.
  // Wave 83.29 — mobile no longer waits for window.load before dismissing.
  // The preloader self-dismisses after MIN_VISIBLE regardless of resource
  // readiness, so LCP fires as soon as the hero img paints (which is
  // already preloaded with fetchpriority=high). Desktop keeps the original
  // pageReady gate because users on broadband don't see a difference.
  const isPhone = window.matchMedia("(max-width: 768px)").matches;
  // Wave 83.37 — mobile preloader uses the full styling (panel + centered
  // wordmark) like desktop, but on a tight ~400ms timeline so it reads as
  // a quick brand pause rather than a long curtain. JS self-dismisses
  // regardless of resource readiness (mobile users on cellular can't
  // afford to wait for window.load before the preloader fades).
  const MIN_VISIBLE = isPhone ? 380 : 1600;
  if (isPhone) {
    setTimeout(finish, MIN_VISIBLE);
    return;
  }
  const pageReady = new Promise((r) => {
    if (document.readyState === "complete") r();
    else window.addEventListener("load", () => r(), { once: true });
  });
  const minWait = new Promise((r) => setTimeout(r, MIN_VISIBLE));
  Promise.all([pageReady, minWait]).then(finish);
  // Failsafe, never block the page for more than 7s even if a resource hangs.
  setTimeout(finish, 7000);
})();

// Wave 80 — hero video kickoff moved inline into index.html so the right
// source (720p phones / 1080p desktop+tablet) mounts before the deferred
// main.js parses. Removed the old [data-hero-video] indirection (no HTML
// page actually used that attribute, so this block was dead code).

// ---------- Split-words: wrap each word in a <span class="word"> with an index ----------
const splitWordsInto = (el) => {
  const walk = (node, wordCounter) => {
    const children = [...node.childNodes];
    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent;
        if (!text.trim()) return;
        const frag = document.createDocumentFragment();
        text.split(/(\s+)/).forEach((chunk) => {
          if (!chunk) return;
          if (/\s+/.test(chunk)) {
            frag.appendChild(document.createTextNode(chunk));
          } else {
            const span = document.createElement("span");
            span.className = "word";
            span.style.setProperty("--word-index", wordCounter.n++);
            span.textContent = chunk;
            frag.appendChild(span);
          }
        });
        child.parentNode.replaceChild(frag, child);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child, wordCounter);
      }
    });
  };
  walk(el, { n: 0 });
};
document.querySelectorAll(".split-words").forEach(splitWordsInto);

// ---------- Stagger reveal for groups marked .reveal-soft ----------
// Each .reveal-soft element inside a common parent gets a sequential delay.
document.querySelectorAll(".counter__grid, .services__list, .work__grid, .blog__grid, .testimonials__grid, .pricing__grid").forEach((group) => {
  [...group.querySelectorAll(".reveal-soft")].forEach((el, i) => {
    el.style.setProperty("--reveal-delay", `${i * 120}ms`);
  });
});

// ---------- Scroll reveal (bidirectional, but heavy tiles are one-way) ----------
// Most elements animate in AND out so the intro replays on re-entry. Tiles that
// carry large images + filters (film tiles, case cards) animate in ONCE and stay
// visible — otherwise the combined transform+filter flipping causes flicker when
// you scroll quickly past them.
const ONE_WAY_REVEAL = new Set();
document.querySelectorAll(".film-tile, .case-card, .project").forEach((el) => ONE_WAY_REVEAL.add(el));

const io = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add("is-visible");
        if (ONE_WAY_REVEAL.has(e.target)) io.unobserve(e.target);
      } else if (!ONE_WAY_REVEAL.has(e.target)) {
        e.target.classList.remove("is-visible");
      }
    });
  },
  // Wave 83.58 — threshold lowered from 0.18 → 0.05 so tall mobile
  // elements (e.g. 9-card-tall .approach-services__grid) still fire
  // is-visible. The previous 0.18 required 18% of the element to be
  // visible inside the rootMargin, which exceeded mobile viewport
  // height for large blocks. The Wave 4.A CSS kill-switch that
  // workaround-fixed this is now removed in css/main.css.
  { threshold: 0.05, rootMargin: "-8% 0px -8% 0px" }
);
document
  .querySelectorAll(".reveal, .reveal-soft, .split-words")
  .forEach((el) => io.observe(el));

// ---------- Scroll-linked "hide" for the Portfolio heading ----------
// As the grid scrolls up toward the sticky heading, progress 0 → 1 drives
// fade + upward translate + clip-path (top-down wipe) on the heading.
const portfolioHeading = document.querySelector(".portfolio .section__head");
const portfolioGrid = document.querySelector(".portfolio__grid");

// "View" badge on portfolio is now static/centred via CSS — no JS follow logic needed.

// ---------- Carousels (Client Experiences, Reviews) ----------
// Horizontal scroll track with prev/next arrow nav and mouse drag.
document.querySelectorAll("[data-carousel]").forEach((root) => {
  const track = root.querySelector("[data-carousel-track]");
  const prev = root.querySelector("[data-carousel-prev]");
  const next = root.querySelector("[data-carousel-next]");
  if (!track) return;
  const looping = root.dataset.carouselLoop !== undefined;

  const stepSize = () => {
    const first = track.firstElementChild;
    if (!first) return track.clientWidth;
    const cs = getComputedStyle(track);
    const gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

  const updateDisabled = () => {
    if (looping) return;                             // loop-enabled carousels never disable
    const max = maxScroll();
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= max - 1;
    if (prev) { prev.disabled = atStart; prev.classList.toggle("is-disabled", atStart); }
    if (next) { next.disabled = atEnd;   next.classList.toggle("is-disabled", atEnd); }
  };

  prev?.addEventListener("click", (e) => {
    e.preventDefault();
    if (looping && track.scrollLeft <= 4) {
      track.scrollTo({ left: maxScroll(), behavior: "smooth" });
      return;
    }
    track.scrollBy({ left: -stepSize(), behavior: "smooth" });
  });
  next?.addEventListener("click", (e) => {
    e.preventDefault();
    if (looping && track.scrollLeft >= maxScroll() - 4) {
      track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    track.scrollBy({ left:  stepSize(), behavior: "smooth" });
  });

  track.addEventListener("scroll", updateDisabled, { passive: true });
  window.addEventListener("resize", updateDisabled);
  // delay initial check until layout settles
  requestAnimationFrame(updateDisabled);

  // Mouse drag to pan — only hijack once the user's motion is clearly horizontal,
  // so vertical page scroll still works when the cursor starts over the track.
  let down = false, startX = 0, startY = 0, startScroll = 0, moved = false, locked = false;
  track.addEventListener("mousedown", (e) => {
    down = true; moved = false; locked = false;
    startX = e.pageX; startY = e.pageY; startScroll = track.scrollLeft;
  });
  const end = () => { down = false; locked = false; track.classList.remove("is-grabbing"); };
  track.addEventListener("mouseleave", end);
  track.addEventListener("mouseup", end);
  window.addEventListener("mouseup", end);                            // release outside the track too
  track.addEventListener("mousemove", (e) => {
    if (!down) return;
    const dx = e.pageX - startX;
    const dy = e.pageY - startY;
    if (!locked) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;              // no lock yet
      if (Math.abs(dy) > Math.abs(dx)) { down = false; return; }     // vertical → let the page scroll
      locked = true;
      track.classList.add("is-grabbing");
    }
    e.preventDefault();
    moved = true;
    track.scrollLeft = startScroll - dx;
  });
  // Suppress accidental link click if user dragged >5px
  track.addEventListener("click", (e) => {
    if (moved) { e.preventDefault(); moved = false; }
  }, true);
});

// ---------- Marquee (continuous auto-scroll) ----------
// Two modes:
//  - default: CSS keyframe animation, hover/drag pauses it (e.g. Reviews)
//  - [data-marquee-drag]: JS-driven offset so the user can actually drag
//    left/right to navigate manually — auto-scroll resumes on release
//    (e.g. Client Experiences, where the user might want to grab a specific
//    testimonial to press play).
document.querySelectorAll("[data-marquee]").forEach((root) => {
  const track = root.querySelector("[data-marquee-track]");
  if (!track) return;
  // Duplicate original children once for seamless 50% translate loop
  const originals = [...track.children];
  for (const el of originals) {
    const clone = el.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    track.appendChild(clone);
  }
  // Wave 71 — flip the animation on AFTER the clones are in the DOM, so the
  // first frame the visitor sees is the original starting position (frame 0)
  // and not whatever phase the CSS engine had reached before the clones
  // arrived.
  track.classList.add("is-ready");

  if (root.hasAttribute("data-marquee-drag")) {
    // JS-driven tick: very slow auto-scroll, pauses on hover, allows manual drag
    // (matches the Reviews feel but with hands-on control).
    track.style.animation = "none";
    track.style.willChange = "transform";
    let offset = 0;
    let dragging = false;
    let hovering = false;
    let lastX = 0;
    let moved = false;
    const speed = 0.18;                     // very slow — ~11px/sec at 60fps

    const loopGuard = () => {
      const half = track.scrollWidth / 2;
      if (!half) return;
      if (offset <= -half) offset += half;
      else if (offset > 0) offset -= half;
    };
    const step = () => {
      if (!dragging && !hovering) offset -= speed;
      loopGuard();
      track.style.transform = "translate3d(" + offset.toFixed(2) + "px, 0, 0)";
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);

    // Hover pauses the auto-scroll so the visitor can read a testimonial
    root.addEventListener("mouseenter", () => { hovering = true; });
    root.addEventListener("mouseleave", () => { hovering = false; });

    const getX = (e) => e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const onDown = (e) => {
      dragging = true;
      moved = false;
      lastX = getX(e);
      track.classList.add("is-grabbing");
    };
    const onMove = (e) => {
      if (!dragging) return;
      const x = getX(e);
      const dx = x - lastX;
      if (Math.abs(dx) > 3) moved = true;
      offset += dx;
      lastX = x;
      loopGuard();
    };
    const onUp = () => {
      dragging = false;
      track.classList.remove("is-grabbing");
    };
    track.addEventListener("mousedown", onDown);
    track.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    // Suppress accidental clicks after a drag
    track.addEventListener("click", (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  } else {
    // CSS-animation mode — hover + drag both pause
    let resumeTimer;
    const pause = () => { track.classList.add("is-paused"); };
    const resume = () => {
      // Wave 83.7 — hold the pause for 4s after the visitor lets go on
      // mobile so the card they tapped stays put long enough to read.
      // Desktop hover-out triggers the same 4s delay; if they re-enter
      // the track within that window the timer keeps resetting via the
      // existing pause() call on mousedown/touchstart.
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => track.classList.remove("is-paused"), 4000);
    };
    track.addEventListener("mousedown", () => { pause(); track.classList.add("is-grabbing"); });
    track.addEventListener("touchstart", pause, { passive: true });
    const release = () => { track.classList.remove("is-grabbing"); resume(); };
    window.addEventListener("mouseup", release);
    window.addEventListener("touchend", release);
  }
});

// ---------- Film tiles: cursor-follow badge with smooth easing (no orbit) ----------
// The badge tracks the cursor with a low lerp factor — feels weightless when the
// user moves, fully STATIC when the cursor is still (no idle rotation).
// Fades via CSS :hover on .film-tile__media.
document.querySelectorAll(".film-tile__media").forEach((media) => {
  const badge = media.querySelector(".film-tile__view");
  if (!badge) return;
  const LERP = 0.14;                 // ease-in smoothing toward cursor
  const REST_EPSILON = 0.25;         // pixel threshold — stop rAF when settled
  let tx = 0, ty = 0;                // target (cursor) position
  let cx = 0, cy = 0;                // current (displayed) position
  let running = false;

  const tick = () => {
    cx += (tx - cx) * LERP;
    cy += (ty - cy) * LERP;
    const bw = badge.offsetWidth  / 2;
    const bh = badge.offsetHeight / 2;
    badge.style.transform =
      "translate3d(" + (cx - bw).toFixed(2) + "px, " + (cy - bh).toFixed(2) + "px, 0)";
    if (Math.abs(tx - cx) > REST_EPSILON || Math.abs(ty - cy) > REST_EPSILON) {
      requestAnimationFrame(tick);
    } else {
      running = false;                // settle: stop animating, badge stays still
    }
  };

  const kick = () => { if (!running) { running = true; requestAnimationFrame(tick); } };

  media.addEventListener("mouseenter", (e) => {
    const r = media.getBoundingClientRect();
    tx = e.clientX - r.left;
    ty = e.clientY - r.top;
    cx = tx; cy = ty;                // snap to cursor on enter — no drift-in
    const bw = badge.offsetWidth  / 2;
    const bh = badge.offsetHeight / 2;
    badge.style.transform =
      "translate3d(" + (cx - bw).toFixed(2) + "px, " + (cy - bh).toFixed(2) + "px, 0)";
  });
  media.addEventListener("mousemove", (e) => {
    const r = media.getBoundingClientRect();
    tx = e.clientX - r.left;
    ty = e.clientY - r.top;
    kick();
  });
});

// ---------- Projects-grid tiles: same cursor-follow "View more" badge ----------
// Mirrors the film-tile pattern above. Lerp-eased translate, snap on enter.
document.querySelectorAll(".projects-grid__media").forEach((media) => {
  const badge = media.querySelector(".projects-grid__cursor");
  if (!badge) return;
  const LERP = 0.14;
  const REST_EPSILON = 0.25;
  let tx = 0, ty = 0;
  let cx = 0, cy = 0;
  let running = false;

  const tick = () => {
    cx += (tx - cx) * LERP;
    cy += (ty - cy) * LERP;
    const bw = badge.offsetWidth  / 2;
    const bh = badge.offsetHeight / 2;
    badge.style.transform =
      "translate3d(" + (cx - bw).toFixed(2) + "px, " + (cy - bh).toFixed(2) + "px, 0)";
    if (Math.abs(tx - cx) > REST_EPSILON || Math.abs(ty - cy) > REST_EPSILON) {
      requestAnimationFrame(tick);
    } else {
      running = false;
    }
  };
  const kick = () => { if (!running) { running = true; requestAnimationFrame(tick); } };

  media.addEventListener("mouseenter", (e) => {
    const r = media.getBoundingClientRect();
    tx = e.clientX - r.left;
    ty = e.clientY - r.top;
    cx = tx; cy = ty;
    const bw = badge.offsetWidth  / 2;
    const bh = badge.offsetHeight / 2;
    badge.style.transform =
      "translate3d(" + (cx - bw).toFixed(2) + "px, " + (cy - bh).toFixed(2) + "px, 0)";
  });
  media.addEventListener("mousemove", (e) => {
    const r = media.getBoundingClientRect();
    tx = e.clientX - r.left;
    ty = e.clientY - r.top;
    kick();
  });
});

// ---------- Approach: horizontal-scroll pin driven by vertical scroll ----------
// While the approach-pin section crosses the viewport, we compute scroll
// progress (0 → 1) and assign it to --hscroll-progress. CSS multiplies it by
// -250vw to translate the track and scaleX the progress bar. The current step
// counter updates accordingly. Disabled on small screens (CSS handles fallback).
(() => {
  const host = document.querySelector("[data-hscroll]");
  if (!host) return;
  const pin = host.querySelector(".approach-pin");
  const track = host.querySelector("[data-hscroll-track]");
  const counter = host.querySelector("[data-hscroll-current]");
  if (!pin || !track) return;

  const steps = [...track.children].filter(el => !el.classList.contains('hstep--outro'));
  const stepCount = steps.length || 7;
  // Hold ratio dialled back to 1.0 — no outro slide to dwell on, so the
  // pin can release immediately when progress reaches 1.0.
  const phaseLabels = [...host.querySelectorAll("[data-phase-range]")];
  let lastActiveIdx = -1;
  let rafP = 0;

  const update = () => {
    rafP = 0;
    // Disable on narrow screens — CSS reflows to vertical list
    if (window.innerWidth <= 900) {
      host.style.setProperty("--hscroll-progress", "0");
      steps.forEach((s) => s.removeAttribute("data-hstep-active"));
      phaseLabels.forEach((p) => p.removeAttribute("data-phase-active"));
      return;
    }
    const rect = pin.getBoundingClientRect();
    const vh = window.innerHeight;
    const total = pin.offsetHeight - vh;
    // No outro to hold on — pin releases as soon as the carousel reaches
    // its end state.
    const HOLD_RATIO = 1.0;
    const raw = (-rect.top / total) / HOLD_RATIO;
    const p = Math.max(0, Math.min(1, raw));
    host.style.setProperty("--hscroll-progress", p.toFixed(4));

    // (Outro slide removed — the CTA now lives in .approach-unlock section
    // directly below the process pin. No special end-state logic needed.)

    const idx = Math.min(stepCount - 1, Math.max(0, Math.round(p * (stepCount - 1))));
    if (idx !== lastActiveIdx) {
      steps.forEach((s, i) => {
        if (i === idx) s.setAttribute("data-hstep-active", ""); else s.removeAttribute("data-hstep-active");
      });
      phaseLabels.forEach((label) => {
        const range = (label.dataset.phaseRange || "").split(",").map(Number);
        if (range.length === 2 && idx >= range[0] && idx < range[1]) {
          label.setAttribute("data-phase-active", "");
        } else {
          label.removeAttribute("data-phase-active");
        }
      });
      if (counter) counter.textContent = String(idx + 1).padStart(2, "0");
      lastActiveIdx = idx;
    }
  };
  const onScrollHs = () => { if (!rafP) rafP = requestAnimationFrame(update); };
  window.addEventListener("scroll", onScrollHs, { passive: true });
  window.addEventListener("resize", onScrollHs, { passive: true });
  update();
})();

// ---------- Sets-Apart: scroll-linked lamp fade ----------
// Lamp is fully lit when the user first reaches the section. As they scroll
// past the headline toward the matrix, the lamp dims to 0 — the table takes over.
const setsApart = document.querySelector(".sets-apart");
if (setsApart) {
  let rafLamp = 0;
  const updateLamp = () => {
    rafLamp = 0;
    const rect = setsApart.getBoundingClientRect();
    const vh = window.innerHeight;
    const start = vh * 0.2;                        // fade begins once top crosses 20% of viewport
    const end = -vh * 0.25;                        // fully faded after 25% more scroll
    const raw = (start - rect.top) / (start - end);
    const progress = Math.max(0, Math.min(1, raw));
    setsApart.style.setProperty("--lamp-fade", (1 - progress).toFixed(3));
  };
  const onScrollLamp = () => { if (!rafLamp) rafLamp = requestAnimationFrame(updateLamp); };
  window.addEventListener("scroll", onScrollLamp, { passive: true });
  window.addEventListener("resize", onScrollLamp, { passive: true });
  updateLamp();
}

if (portfolioHeading && portfolioGrid) {
  let raf = 0;
  const update = () => {
    raf = 0;
    const gridRect = portfolioGrid.getBoundingClientRect();
    const viewH = window.innerHeight;
    const start = viewH * 0.55;
    const end = viewH * 0.12;
    const raw = (start - gridRect.top) / (start - end);
    const progress = Math.max(0, Math.min(1, raw));
    portfolioHeading.style.setProperty("--hide-progress", progress.toFixed(3));
  };
  const onScroll = () => {
    if (!raf) raf = requestAnimationFrame(update);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  update();
}

// ---------- Logo marquee (trusted-by strip): duplicate for seamless loop ----------
// Note: the testimonial/reviews marquee is handled separately above via
// [data-marquee] so we don't double-clone those tracks here.
document.querySelectorAll(".trusted__track").forEach((track) => {
  const clone = track.innerHTML;
  track.innerHTML = clone + clone;
  // Wave 71 — start the trusted-by animation only after the duplicated
  // markup is in place, so the first visible frame is always One&Only and
  // the cycle plays from logo 1 → 15 → wrap.
  track.classList.add("is-ready");
});

// ---------- Pricing toggle ----------
const toggleRoot = document.querySelector(".pricing__toggle");
if (toggleRoot) {
  const btns = toggleRoot.querySelectorAll("button");
  btns.forEach((b) =>
    b.addEventListener("click", () => {
      btns.forEach((x) => x.setAttribute("aria-pressed", "false"));
      b.setAttribute("aria-pressed", "true");
      const mode = b.dataset.mode;
      document.querySelectorAll("[data-price-monthly]").forEach((el) => {
        el.textContent = mode === "annual" ? el.dataset.priceAnnual : el.dataset.priceMonthly;
      });
      document.querySelectorAll("[data-period]").forEach((el) => {
        el.textContent = mode === "annual" ? "/yr" : "/mo";
      });
    })
  );
}

// ---------- FAQ: JS-driven accordion with smooth max-height animation ----------
// Native <details> animations are unreliable because browsers set display:none on
// closed content. We use buttons + data-open attribute and animate max-height to
// the actual scrollHeight so opening and closing both slide smoothly in both dirs.
document.querySelectorAll("[data-faq-toggle]").forEach((btn) => {
  btn.addEventListener("click", () => {
    const item = btn.closest(".faq-item");
    if (!item) return;
    const body = item.querySelector(".faq-item__body");
    const isOpen = item.hasAttribute("data-open");

    // Close all siblings first
    item.parentElement.querySelectorAll(".faq-item").forEach((other) => {
      if (other === item) return;
      const ob = other.querySelector(".faq-item__body");
      if (ob) ob.style.maxHeight = "0px";
      other.removeAttribute("data-open");
      const ot = other.querySelector("[data-faq-toggle]");
      if (ot) ot.setAttribute("aria-expanded", "false");
    });

    if (isOpen) {
      // Close: set from scrollHeight → 0 so transition runs
      body.style.maxHeight = body.scrollHeight + "px";
      requestAnimationFrame(() => { body.style.maxHeight = "0px"; });
      item.removeAttribute("data-open");
      btn.setAttribute("aria-expanded", "false");
    } else {
      item.setAttribute("data-open", "");
      btn.setAttribute("aria-expanded", "true");
      // Next frame: measure scrollHeight and animate to it
      requestAnimationFrame(() => {
        body.style.maxHeight = body.scrollHeight + "px";
      });
    }
  });
});
// Initial state: open items (with data-open attr) get max-height set so they're visible
document.querySelectorAll(".faq-item[data-open] .faq-item__body").forEach((body) => {
  body.style.maxHeight = body.scrollHeight + "px";
});

// ---------- Counter: animate numbers when in view ----------
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const animateCount = (el) => {
  const target = parseFloat(el.dataset.count);
  const suffix = el.dataset.suffix || "";
  const duration = 1400;
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / duration);
    const val = target * easeOut(p);
    el.textContent = (Number.isInteger(target) ? Math.round(val) : val.toFixed(1)) + suffix;
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};
const countObs = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        animateCount(e.target);
        countObs.unobserve(e.target);
      }
    });
  },
  { threshold: 0.4 }
);
document.querySelectorAll("[data-count]").forEach((el) => countObs.observe(el));

// ---------- Live editorial masthead clock + geo-IP city/country ----------
// Three pieces of live data run side by side on every inner hero:
//   1. Date    (top-left chip, [data-date]) — long-form date in Athens
//      timezone, e.g. "Tuesday, 1 May 2026".
//   2. Location (bottom-left chip, [data-geo-city] / [data-geo-country]) —
//      the visitor's resolved place, falling back to the studio.
//   3. Time     (bottom-right chip, [data-clock]) — visitor-local time in
//      24h HH:MM (no seconds, the rhombus mark carries the live pulse).
// All tick once per minute on a 1s heartbeat so the rollover is instant.
// Wave 83.54 — third-party geo-IP API removed; widget shows the
// studio's home city/time regardless of visitor location.
(() => {
  const clockEls       = document.querySelectorAll("[data-clock], [data-clock-alt]");
  const clockAthensEls = document.querySelectorAll("[data-clock-athens]");
  const dateEls        = document.querySelectorAll("[data-date]");
  const cityEls        = document.querySelectorAll("[data-geo-city]");
  const countryEls     = document.querySelectorAll("[data-geo-country]");
  if (!clockEls.length && !clockAthensEls.length && !dateEls.length && !cityEls.length && !countryEls.length) return;

  // Wave 83.54 — masthead now always displays HoA's studio location (Athens,
  // Greece) and Athens local time, not the visitor's IP-derived city. This
  // matches the brand framing on every page header and removes the only
  // third-party network call that fired before consent.
  const currentTz      = "Europe/Athens";
  const currentCity    = "Athens";
  const currentCountry = "Greece";

  const timeFmtFor = (tz) => new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const dateFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Athens",
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const writeLoc = () => {
    cityEls.forEach(e => e.textContent = currentCity);
    countryEls.forEach(e => e.textContent = currentCountry);
  };
  const tick = () => {
    const now = new Date();
    if (clockAthensEls.length) {
      const ta = timeFmtFor("Europe/Athens").format(now);
      clockAthensEls.forEach(e => e.textContent = ta);
    }
    if (clockEls.length) {
      const tv = timeFmtFor(currentTz).format(now);
      clockEls.forEach(e => e.textContent = tv);
    }
    if (dateEls.length) {
      const d = dateFmt.format(now);
      dateEls.forEach(e => e.textContent = d);
    }
  };
  writeLoc();
  tick();
  // 1s heartbeat — minute rollover lands instantly, rhombus pulse handles the
  // live read.
  setInterval(tick, 1000);

  // Wave 83.54 — third-party geo-IP fetch removed for GDPR Art. 6 compliance.
  // City/country resolved entirely from local Intl timezone. No third party.
})();

// ---------- Alt footer cursor-tracking spotlight (slow lerp trail) ----------
(() => {
  const root = document.querySelector("[data-spotlight]");
  if (!root) return;
  const LERP = 0.085;                    // very slow trail — spotlight lags the cursor
  let tx = 50, ty = 50;                  // target (cursor pos)
  let cx = 50, cy = 50;                  // current (displayed pos)
  let running = false;
  const tick = () => {
    if (!running) return;
    cx += (tx - cx) * LERP;
    cy += (ty - cy) * LERP;
    root.style.setProperty("--mx", cx.toFixed(2) + "%");
    root.style.setProperty("--my", cy.toFixed(2) + "%");
    if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) {
      requestAnimationFrame(tick);
    } else {
      running = false;
    }
  };
  root.addEventListener("mousemove", (e) => {
    const r = root.getBoundingClientRect();
    tx = ((e.clientX - r.left) / r.width) * 100;
    ty = ((e.clientY - r.top) / r.height) * 100;
    if (!running) { running = true; requestAnimationFrame(tick); }
  });
})();

// ---------- Client Stories: single-testimonial viewer with prev/next ----------
// Stories stacked in one grid cell; .is-active controls pure opacity (no translate).
(() => {
  const viewer = document.querySelector("[data-cs-viewer]");
  if (!viewer) return;
  const items = Array.from(viewer.querySelectorAll("[data-cs-item]"));
  if (items.length < 2) return;
  const prevBtn = document.querySelector("[data-cs-prev]");
  const nextBtn = document.querySelector("[data-cs-next]");
  const nowEl = document.querySelector("[data-cs-now]");
  const totalEl = document.querySelector("[data-cs-total]");
  const ghostEl = document.querySelector("[data-cs-ghost]");
  const pad2 = (n) => String(n).padStart(2, "0");
  if (totalEl) totalEl.textContent = pad2(items.length);
  let idx = 0;
  const show = (to) => {
    idx = (to + items.length) % items.length;
    items.forEach((el, i) => el.classList.toggle("is-active", i === idx));
    if (nowEl) nowEl.textContent = pad2(idx + 1);
    if (ghostEl) ghostEl.textContent = pad2(idx + 1);
  };
  prevBtn && prevBtn.addEventListener("click", () => show(idx - 1));
  nextBtn && nextBtn.addEventListener("click", () => show(idx + 1));
  show(0);
})();

// ---------- Rotating dotted globe (Approach aside → Worldwide reach) ----------
// Wave 72 — d3 + world-land are now lazy-loaded via IntersectionObserver
// instead of being shipped as <script defer> in the page <head>. This saves
// ~517KB of JS parse+execute on initial page load, which was the bulk of the
// desktop TBT. The globe section is well below the fold on home + about, so
// the visitor never sees the difference; the moment they scroll near it, both
// scripts inject and the globe boots within ~300-500ms.
(() => {
  const host = document.querySelector("[data-globe]");
  if (!host) return;
  const canvas = host.querySelector(".globe-canvas");
  if (!canvas) return;

  let depsPromise = null;

  const injectScript = (src, attrs = {}) => new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src.endsWith(src) || s.src === src)) {
      return resolve();
    }
    const s = document.createElement("script");
    s.src = src;
    Object.assign(s, attrs);
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

  const loadDeps = () => {
    if (depsPromise) return depsPromise;
    // Inject world-land first (local, fast), then d3 from CDN. Returns a
    // single promise that resolves when BOTH scripts have executed, so the
    // boot() chain below can wait reliably instead of polling.
    depsPromise = Promise.all([
      injectScript("/assets/world-land.js").catch(() => {}),
      injectScript(
        "https://cdn.jsdelivr.net/npm/d3@7.9.0/dist/d3.min.js",
        {
          integrity:
            "sha384-CjloA8y00+1SDAUkjs099PVfnY2KmDC2BZnws9kh8D/lX1s46w6EPhpXdqMfjK6i",
          crossOrigin: "anonymous",
        }
      ).catch(() => {}),
    ]);
    return depsPromise;
  };

  const waitForD3 = () => new Promise((resolve) => {
    if (typeof d3 !== "undefined") return resolve();
    let tries = 0;
    const id = setInterval(() => {
      if (typeof d3 !== "undefined" || tries++ > 200) {
        clearInterval(id);
        resolve();
      }
    }, 50);
  });

  const waitForLandData = () => new Promise((resolve) => {
    if (window.HOA_WORLD_LAND && window.HOA_WORLD_LAND.features) return resolve();
    let tries = 0;
    const id = setInterval(() => {
      if ((window.HOA_WORLD_LAND && window.HOA_WORLD_LAND.features) || tries++ > 200) {
        clearInterval(id);
        resolve();
      }
    }, 50);
  });

  const context = canvas.getContext("2d", { alpha: true });
  let width = 0, height = 0, radius = 0;
  let projection = null, path = null;

  const pointInPolygon = (p, polygon) => {
    const [x, y] = p;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  const pointInFeature = (p, f) => {
    const g = f.geometry;
    if (g.type === "Polygon") {
      if (!pointInPolygon(p, g.coordinates[0])) return false;
      for (let i = 1; i < g.coordinates.length; i++) if (pointInPolygon(p, g.coordinates[i])) return false;
      return true;
    }
    if (g.type === "MultiPolygon") {
      for (const poly of g.coordinates) {
        if (pointInPolygon(p, poly[0])) {
          let hole = false;
          for (let i = 1; i < poly.length; i++) if (pointInPolygon(p, poly[i])) { hole = true; break; }
          if (!hole) return true;
        }
      }
    }
    return false;
  };
  const generateDots = (feature, spacing = 14) => {
    const dots = [];
    const [[minLng, minLat], [maxLng, maxLat]] = d3.geoBounds(feature);
    const step = spacing * 0.08;
    for (let lng = minLng; lng <= maxLng; lng += step) {
      for (let lat = minLat; lat <= maxLat; lat += step) {
        const p = [lng, lat];
        if (pointInFeature(p, feature)) dots.push(p);
      }
    }
    return dots;
  };

  let landFeatures = null;
  const allDots = [];
  const rotation = [18, -12];                   // tilt toward Europe + light northern lean
  let autoRotate = true;
  const rotationSpeed = 0.18;                   // slow luxury pace
  let timer = null;
  let booted = false;
  let baseRadius = 0;                           // reference radius at 100% zoom — set in sizeCanvas

  const sizeCanvas = () => {
    const rect = host.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w < 10 || h < 10) return false;
    width = w; height = h;
    // Base radius = host / 3 → starts as an icon-sized sphere, never touches the edges.
    // Combined with ZOOM_MAX 1.4 below, max diameter = host × 0.93, so the globe
    // never clips at the frame even at full zoom.
    baseRadius = Math.min(width, height) / 3;
    const currentScale = projection ? projection.scale() / (radius || baseRadius) : 1;
    radius = baseRadius;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!projection) {
      if (typeof d3 === "undefined") return false;  // d3 not loaded yet; boot() will retry
      projection = d3.geoOrthographic().clipAngle(90);
      path = d3.geoPath().projection(projection).context(context);
    }
    projection.scale(radius * currentScale).translate([width / 2, height / 2]).rotate(rotation);
    return true;
  };

  const render = () => {
    if (!width || !height) return;
    context.clearRect(0, 0, width, height);

    // Sphere — no heavy rim; the sphere surface is implied by the graticule + land outlines
    context.beginPath();
    context.arc(width / 2, height / 2, projection.scale(), 0, Math.PI * 2);
    context.fillStyle = "#080808";
    context.fill();
    context.strokeStyle = "rgba(255,255,255,0.42)";
    context.lineWidth = 0.6;
    context.stroke();

    if (!landFeatures) return;

    context.lineJoin = "round";
    context.lineCap = "round";

    // Graticule — longitude/latitude lines, soft and even, give the "sphere" feel
    context.beginPath();
    path(d3.geoGraticule().step([12, 12])());
    context.strokeStyle = "rgba(255,255,255,0.16)";
    context.lineWidth = 0.45;
    context.stroke();

    // Country outlines — hairlines, elegant, slightly brighter than the grid
    context.beginPath();
    for (const f of landFeatures.features) path(f);
    context.strokeStyle = "rgba(255,255,255,0.7)";
    context.lineWidth = 0.5;
    context.stroke();

    // Subtle inner fill so land reads as land, not just outlines
    context.beginPath();
    for (const f of landFeatures.features) path(f);
    context.fillStyle = "rgba(255,255,255,0.045)";
    context.fill();
  };

  const tick = () => {
    if (!autoRotate) return;
    rotation[0] += rotationSpeed;
    if (projection) { projection.rotate(rotation); render(); }
  };

  // Drag — X + Y rotation. Auto-rotate resumes immediately on release (no wait).
  const onDown = (e) => {
    if (!projection) return;
    autoRotate = false;
    host.classList.add("is-grabbing");
    const startX = e.clientX, startY = e.clientY;
    const startRot = [...rotation];
    const sens = 0.45;
    const move = (m) => {
      rotation[0] = startRot[0] + (m.clientX - startX) * sens;
      rotation[1] = Math.max(-85, Math.min(85, startRot[1] - (m.clientY - startY) * sens));
      projection.rotate(rotation);
      render();
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      host.classList.remove("is-grabbing");
      autoRotate = true;                     // resume instantly
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  host.addEventListener("mousedown", onDown);

  // Scroll / pinch zoom — bounded so the sphere ALWAYS fits inside the host.
  // baseRadius is host/3 → max diameter = (host/3) × 1.4 × 2 = host × 0.93.
  const ZOOM_MIN = 0.85, ZOOM_MAX = 1.4;
  const onWheel = (e) => {
    if (!projection) return;
    e.preventDefault();
    const current = projection.scale();
    const factor = e.deltaY > 0 ? 0.94 : 1.06;
    const next = Math.max(baseRadius * ZOOM_MIN, Math.min(baseRadius * ZOOM_MAX, current * factor));
    projection.scale(next);
    render();
  };
  host.addEventListener("wheel", onWheel, { passive: false });

  // Touch drag (single finger) — immediate resume on end
  host.addEventListener("touchstart", (e) => {
    if (!projection || e.touches.length !== 1) return;
    autoRotate = false;
    const t = e.touches[0];
    const startX = t.clientX, startY = t.clientY;
    const startRot = [...rotation];
    const sens = 0.45;
    const move = (ev) => {
      if (ev.touches.length !== 1) return;
      ev.preventDefault();
      const p = ev.touches[0];
      rotation[0] = startRot[0] + (p.clientX - startX) * sens;
      rotation[1] = Math.max(-85, Math.min(85, startRot[1] - (p.clientY - startY) * sens));
      projection.rotate(rotation);
      render();
    };
    const end = () => {
      host.removeEventListener("touchmove", move);
      host.removeEventListener("touchend", end);
      autoRotate = true;                     // resume instantly
    };
    host.addEventListener("touchmove", move, { passive: false });
    host.addEventListener("touchend", end);
  }, { passive: true });

  // ResizeObserver — re-size whenever the container changes dimensions
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(() => { if (sizeCanvas()) render(); }).observe(host);
  }

  // Boot once d3 + inline land data are both ready
  const boot = async () => {
    await waitForD3();
    if (typeof d3 === "undefined") {
      console.warn("[globe] d3 failed to load — globe will not render");
      return;
    }
    // Defer one frame so sticky/grid layout has run
    await new Promise((r) => requestAnimationFrame(r));
    if (!sizeCanvas()) {
      // container not sized yet; ResizeObserver will retry once it is
      return;
    }
    render();  // show sphere immediately
    await waitForLandData();
    try {
      const data = window.HOA_WORLD_LAND;
      if (!data || !data.features) throw new Error("HOA_WORLD_LAND missing or malformed");
      landFeatures = data;
      // Dot-halftone rendering removed — we now use country outlines + graticule only.
      console.info("[globe] ready —", landFeatures.features.length, "land features (lines-only aesthetic)");
      render();
      if (!timer) timer = d3.timer(tick);
      booted = true;
    } catch (err) {
      console.error("[globe] land data failed:", err);
      // sphere still renders, just without dots/land
    }
  };

  // Pause/resume timer based on visibility (battery)
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!booted || !timer) return;
      if (e.isIntersecting) timer.restart(tick);
      else timer.stop();
    });
  }, { threshold: 0 });
  obs.observe(host);

  // Wave 78 — boot the globe ONLY after the lazy-loaded deps land, NOT at
  // page-load. Previously waitForD3()'s 10s polling timeout would resolve
  // before the user scrolled near the globe section, leaving d3 still
  // undefined and the globe silently empty. Now: IntersectionObserver fires
  // when the host enters its 800px pre-fetch margin, loadDeps() injects
  // d3 + world-land, and only then boot() runs. Browsers without IO get a
  // sync fallback that loads everything immediately.
  const triggerBoot = () => {
    loadDeps().then(boot);
  };
  if ("IntersectionObserver" in window) {
    const lazyIO = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          triggerBoot();
          lazyIO.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    lazyIO.observe(host);
  } else {
    triggerBoot();
  }
})();

/* ============================================================
   About — three-values Venn convergence animation. Sticky-pinned section:
   the .about-blend__rail is ~360vh tall, the inner sticky pin sits at
   top:0 / 100vh, so the stage stays locked to the viewport for ~3 scroll
   ticks while the rail scrolls past it. Progress 0→1 is the rail's
   travel from "just entered the viewport" to "just released".

   Three sub-progress values are written so the wordmark can be ABSENT
   early, then FADE in mid, then GROW late:
     --blend-p     0→1   (drives circle convergence + label fade)
     --blend-fade  0→1   (wordmark opacity, mapped from p∈[0.40, 0.70])
     --blend-grow  0→1   (wordmark scale,   mapped from p∈[0.70, 1.00])
============================================================ */
(() => {
  const rail = document.querySelector('[data-blend-rail]');
  const stage = document.querySelector('[data-blend-stage]');
  const head = document.querySelector('[data-blend-head]');
  if (!rail || !stage) return;

  function smoothstep(t) { return t * t * (3 - 2 * t); }
  function range(raw, a, b) {
    return Math.max(0, Math.min(1, (raw - a) / (b - a)));
  }

  let raf = 0;
  function update() {
    raf = 0;
    const rect = rail.getBoundingClientRect();
    const vh = window.innerHeight;
    const travel = rect.height - vh;
    const scrolled = Math.max(0, -rect.top);
    const raw = travel > 0 ? Math.max(0, Math.min(1, scrolled / travel)) : 0;

    /* Phase windows (raw):
         0.00 → 0.18   head (eyebrow + title + lede) fades up
         0.05 → 0.32   labels inside circles fade out
         0.18 → 0.55   circles row → pyramid Venn (--blend-p)
         0.45 → 0.85   pyramid → single merged ring   (--blend-merge)
         0.28 → 0.55   wordmark fades in earlier      (--blend-fade)
         0.40 → 1.00   wordmark grows steadily        (--blend-grow) */
    const headProgress  = smoothstep(range(raw, 0.00, 0.18));
    const labelProgress = smoothstep(range(raw, 0.05, 0.32));
    const circleP       = smoothstep(range(raw, 0.18, 0.55));
    const mergeP        = smoothstep(range(raw, 0.45, 0.85));
    const fade          = smoothstep(range(raw, 0.28, 0.55));
    const grow          = smoothstep(range(raw, 0.40, 1.00));

    stage.style.setProperty('--blend-p', circleP.toFixed(4));
    stage.style.setProperty('--blend-merge', mergeP.toFixed(4));
    stage.style.setProperty('--blend-label', labelProgress.toFixed(4));
    stage.style.setProperty('--blend-fade', fade.toFixed(4));
    stage.style.setProperty('--blend-grow', grow.toFixed(4));
    if (head) head.style.setProperty('--blend-head', headProgress.toFixed(4));
  }
  function onScroll() {
    if (!raf) raf = requestAnimationFrame(update);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();

/* ============================================================
   Hamburger + Side menu — reveals after hero scroll, panel slides from right
============================================================ */
(function () {
  var hamburger = document.querySelector('[data-menu-toggle]');
  var sideMenu  = document.querySelector('[data-side-menu]');
  var overlay   = document.querySelector('[data-menu-overlay]');
  var closeBtn  = document.querySelector('[data-menu-close]');
  var hero      = document.querySelector('.about-hero__panel, .hero__panel');

  if (!hamburger || !sideMenu) return;

  var links   = sideMenu.querySelectorAll('.side-menu__nav a');
  var isOpen  = false;
  var threshold;

  function computeThreshold() {
    var h = hero ? hero.offsetHeight : window.innerHeight;
    threshold = Math.max(200, h * 0.7);
  }

  function openMenu() {
    sideMenu.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    hamburger.classList.add('is-open');
    hamburger.setAttribute('aria-expanded', 'true');
    sideMenu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-side-menu-open');
    isOpen = true;
  }

  function closeMenu() {
    sideMenu.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    hamburger.classList.remove('is-open');
    hamburger.setAttribute('aria-expanded', 'false');
    sideMenu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-side-menu-open');
    isOpen = false;
  }

  hamburger.addEventListener('click', function () {
    if (isOpen) closeMenu();
    else openMenu();
  });

  if (closeBtn) closeBtn.addEventListener('click', closeMenu);
  if (overlay)  overlay.addEventListener('click', closeMenu);

  // Close on link click so the user lands cleanly on the next page section
  links.forEach(function (a) {
    a.addEventListener('click', function () {
      closeMenu();
    });
  });

  // ESC key closes
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) closeMenu();
  });

  // Mark the current page in the side menu nav
  try {
    var current = (location.pathname.split('/').pop() || 'index.html').toLowerCase();
    links.forEach(function (a) {
      var href = (a.getAttribute('href') || '').toLowerCase();
      if (href === current) a.setAttribute('aria-current', 'page');
    });
  } catch (e) {}

  // Scroll-triggered visibility
  computeThreshold();
  window.addEventListener('resize', computeThreshold, { passive: true });

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      if (window.scrollY > threshold) {
        hamburger.classList.add('is-active');
      } else {
        hamburger.classList.remove('is-active');
        if (isOpen) closeMenu();
      }
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* ============================================================
   APPROACH V2 — Circular step indicator engine. Reads scroll
   progress along the rail, divides it into N equal windows, and
   writes the active step number + circular progress fill. Used
   by both approach-v2-preview.html and the live approach.html.
============================================================ */
(function () {
  var rail = document.querySelector('[data-av2-rail]');
  if (!rail) return;
  var fillEl   = document.querySelector('[data-av2-fill]');
  var numEl    = document.querySelector('[data-av2-marker-num]');
  var stepOf   = document.querySelector('[data-av2-step-of]');
  var stepLabel = document.querySelector('[data-av2-step-label]');
  var steps    = Array.from(document.querySelectorAll('[data-av2-step]'));
  var scrollEl = document.querySelector('.av2-scroll');
  if (!fillEl || !numEl || !steps.length) return;

  var N = 7;
  var DASH = 1000;
  var STEP_NAMES = [
    'Discovery & Audit',
    'Strategy',
    'Creative Direction',
    'Production',
    'Distribution',
    'Qualification',
    'Tracking & Reporting'
  ];

  function setActive(idx) {
    steps.forEach(function (el, i) {
      el.classList.toggle('is-active', i === idx);
    });
    var pad = String(idx + 1).padStart(2, '0');
    if (numEl) numEl.textContent = pad;
    if (stepOf) stepOf.textContent = STEP_NAMES[idx] || ('Step ' + pad);
    if (stepLabel) stepLabel.textContent = 'Step ' + pad;
  }

  var lastIdx = -1;
  var raf = 0;
  function update() {
    raf = 0;
    var rect = rail.getBoundingClientRect();
    var travel = rect.height - window.innerHeight;
    var scrolled = Math.max(0, -rect.top);
    var raw = travel > 0 ? Math.max(0, Math.min(1, scrolled / travel)) : 0;

    var idxFloat = raw * N;
    var idx = Math.min(N - 1, Math.floor(idxFloat));
    var localT = idxFloat - idx;

    if (idx !== lastIdx) {
      setActive(idx);
      lastIdx = idx;
    }

    fillEl.setAttribute('stroke-dashoffset', (DASH * (1 - localT)).toFixed(2));

    if (scrollEl) {
      var fade = Math.max(0, Math.min(1, (1 - raw) / 0.18));
      scrollEl.style.setProperty('--av2-scroll-fade', fade.toFixed(3));
    }
  }
  function onScroll() {
    if (!raf) raf = requestAnimationFrame(update);
  }
  update();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
})();

/* ============================================================
   FLOATING NAV, top-only visibility.
   Visible while the user is at the very top of the page (within
   40px of scroll origin, i.e. inside the hero region). On any
   scroll past that threshold the nav stays hidden — even if the
   user scrolls back up mid-page. The hamburger menu is the
   navigation affordance from that point on.
   This kills the "header pops back in mid-page on scroll-up" bug
   reported across Projects, Case Studies, and case-study singles.
============================================================ */
(function () {
  var nav = document.querySelector('.nav--floating');
  if (!nav) return;
  var ticking = false;
  function read() {
    ticking = false;
    var y = window.scrollY || 0;
    if (y > 40) {
      nav.classList.add('is-hidden');
      document.body.classList.add('nav-hidden');
    } else {
      nav.classList.remove('is-hidden');
      document.body.classList.remove('nav-hidden');
    }
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(read); }
  }, { passive: true });
  read();
})();

/* ============================================================
   SALT-CINEMA video player (shared).
   Drives every <video data-hover-play> tile on the site:
     1. Hover/in-view → preview the tile inline with sound when
        autoplay-with-sound is allowed, falling back to muted.
     2. Click → open a glass fullscreen modal that plays the same
        source with custom chrome (play/pause, volume, fullscreen,
        time, rotating ×). Aspect ratio is detected from the video
        and the stage flips between portrait / square / landscape
        layouts via the .is-landscape / .is-square classes on the
        modal root.
   The modal markup is injected into the body on first init so
   pages only need the inline tile markup.
   Lifted from ck-realtors.html.
============================================================ */
(function () {
  function init() {
    var tiles = document.querySelectorAll('.va__vid-frame video');
    if (!tiles.length) return;

    var hasHover = window.matchMedia('(hover: hover)').matches;
    var player = ensureModal();
    var stage = player.querySelector('.va__player-stage');
    var pVideo = player.querySelector('.va__player-video');
    var pFill = player.querySelector('.va__player-bar-fill');
    var pBar  = player.querySelector('.va__player-bar');
    var pTime = player.querySelector('.va__player-time');
    var pToggle = player.querySelector('.va__player-toggle');
    var pClose  = player.querySelector('.va__player-close');
    var pVolBtn = player.querySelector('.va__player-vol-btn');
    var pVolRange = player.querySelector('.va__player-vol');
    var pFsBtn = player.querySelector('.va__player-fs');
    var modalRaf = null;

    function fmt(s) {
      if (!isFinite(s) || s < 0) return '0:00';
      var m = Math.floor(s / 60), x = Math.floor(s % 60);
      return m + ':' + (x < 10 ? '0' : '') + x;
    }
    function applyOrientationClass() {
      var w = pVideo.videoWidth || 0;
      var h = pVideo.videoHeight || 0;
      player.classList.remove('is-landscape', 'is-square', 'is-portrait');
      if (!w || !h) { player.classList.add('is-portrait'); return; }
      var ratio = w / h;
      if (ratio > 1.1) player.classList.add('is-landscape');
      else if (ratio < 0.9) player.classList.add('is-portrait');
      else player.classList.add('is-square');
    }
    function loop() {
      var cur = pVideo.currentTime || 0;
      var dur = pVideo.duration  || 0;
      pFill.style.width = dur ? ((cur / dur) * 100).toFixed(2) + '%' : '0%';
      pTime.textContent = fmt(cur) + ' / ' + fmt(dur);
      modalRaf = requestAnimationFrame(loop);
    }
    function startLoop() { if (!modalRaf) modalRaf = requestAnimationFrame(loop); }
    function stopLoop()  { if (modalRaf) { cancelAnimationFrame(modalRaf); modalRaf = null; } }

    function openModal(srcUrl) {
      try { pVideo.pause(); } catch (e) {}
      pVideo.src = srcUrl;
      player.classList.add('is-open');
      player.classList.remove('is-paused');
      document.body.classList.add('va-player-open');
      pVideo.muted = false;
      var p = pVideo.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () { pVideo.muted = true; pVideo.play().catch(function () {}); syncVolUI(); });
      }
      startLoop();
    }
    function closeModal() {
      try { pVideo.pause(); } catch (e) {}
      player.classList.remove('is-open', 'is-paused');
      document.body.classList.remove('va-player-open');
      stopLoop();
      setTimeout(function () { try { pVideo.removeAttribute('src'); pVideo.load(); } catch (e) {} }, 480);
    }

    /* ----- two tile modes -----
       Default = Salt-Cinema. Tile shows poster + centred glass play
                 disc. Click opens the modal. No hover preview, no
                 in-tile playhead.
       --hover-play (CK Realtors only) = the older behaviour. Hover
                 plays the video inline with sound, pointer-leave
                 resets, and the bottom hairline playhead fills as
                 the preview runs. ============================= */
    tiles.forEach(function (video) {
      var frame = video.closest('.va__vid-frame') || video.parentElement;
      var isHoverPlay = frame && frame.classList.contains('va__vid-frame--hover-play');
      var fill  = frame ? frame.querySelector('.va__vid-bar-fill') : null;
      var raf = null;

      var tickFill = function () {
        if (!fill) return;
        var ratio = video.duration ? (video.currentTime / video.duration) : 0;
        fill.style.width = (ratio * 100).toFixed(2) + '%';
        raf = requestAnimationFrame(tickFill);
      };
      var stopFill = function () {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
      };

      if (isHoverPlay) {
        if (hasHover) {
          var tryPlay = function () {
            video.muted = false;
            var p = video.play();
            if (p && typeof p.catch === 'function') {
              p.catch(function () { video.muted = true; video.play().catch(function () {}); });
            }
            tickFill();
          };
          frame.addEventListener('pointerenter', tryPlay);
          frame.addEventListener('pointerleave', function () {
            try { video.pause(); video.currentTime = 0; video.muted = true; } catch (e) {}
            stopFill();
            if (fill) fill.style.width = '0%';
          });
        } else {
          // Wave 63 — drop the threshold from 0.6 to 0.3 so the video kicks
          // in earlier on mobile scrolls; iOS Safari often won't honour
          // play() unless the element is at least 30% on-screen, but waiting
          // for 60% means a quick scroll past the tile leaves it stuck on
          // the poster image (CK Realtors complaint).
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              var v = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
                v.muted = true;
                try { v.play().catch(function () {}); } catch (e) {}
                tickFill();
              } else {
                try { v.pause(); } catch (e) {}
                stopFill();
              }
            });
          }, { threshold: [0, 0.3, 0.6, 1] });
          video.muted = true;
          io.observe(video);
        }
      }

      /* click-to-modal applies to BOTH modes */
      frame.addEventListener('click', function () {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
        stopFill();
        var src = (video.querySelector('source') && video.querySelector('source').getAttribute('src')) || video.src;
        if (!src) return;
        openModal(src);
      });
    });

    /* ----- modal chrome ----- */
    pClose.addEventListener('click', closeModal);
    player.addEventListener('click', function (e) { if (e.target === player) closeModal(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && player.classList.contains('is-open')) closeModal();
    });
    pToggle.addEventListener('click', function () {
      if (pVideo.paused) {
        pVideo.play().catch(function () {});
        player.classList.remove('is-paused');
      } else {
        pVideo.pause();
        player.classList.add('is-paused');
      }
    });
    pVideo.addEventListener('ended', function () { player.classList.add('is-paused'); });
    pVideo.addEventListener('loadedmetadata', applyOrientationClass);
    pVideo.addEventListener('volumechange', syncVolUI);

    /* click anywhere on the bottom progress bar → seek */
    pBar.addEventListener('click', function (e) {
      var rect = pBar.getBoundingClientRect();
      var ratio = (e.clientX - rect.left) / rect.width;
      if (pVideo.duration) pVideo.currentTime = ratio * pVideo.duration;
    });

    /* volume button: toggle mute */
    pVolBtn.addEventListener('click', function () {
      pVideo.muted = !pVideo.muted;
      if (!pVideo.muted && pVideo.volume === 0) pVideo.volume = 0.6;
      syncVolUI();
    });
    /* slider: set volume (and unmute if user dragged > 0) */
    pVolRange.addEventListener('input', function () {
      var v = parseFloat(pVolRange.value) / 100;
      pVideo.volume = v;
      pVideo.muted = (v === 0);
      syncVolUI();
    });
    function syncVolUI() {
      var muted = pVideo.muted || pVideo.volume === 0;
      pVolBtn.setAttribute('data-state', muted ? 'muted' : 'on');
      pVolRange.value = muted ? 0 : Math.round((pVideo.volume || 0) * 100);
    }
    syncVolUI();

    /* fullscreen */
    pFsBtn.addEventListener('click', function () {
      var d = document;
      var inFs = d.fullscreenElement || d.webkitFullscreenElement;
      if (inFs) {
        (d.exitFullscreen || d.webkitExitFullscreen || function () {}).call(d);
        return;
      }
      // Wave 83.58 — iOS Safari: requestFullscreen() on a <div> container
      // is a no-op even though iOS 18 reports document.fullscreenEnabled
      // as true. The only reliable iOS path is <video>.webkitEnterFullscreen()
      // which launches the native iOS player (rotation + native chrome +
      // native Done button). webkitEnterFullscreen ONLY exists on iOS, so
      // its presence is the cleanest iOS-Safari detection.
      if (pVideo && typeof pVideo.webkitEnterFullscreen === 'function') {
        // Ensure video has metadata loaded so iOS will honour the call.
        try {
          if (pVideo.readyState < 1) pVideo.load();
          pVideo.webkitEnterFullscreen();
          return;
        } catch (e) { /* fall through to standard path */ }
      }
      var el = stage;
      (el.requestFullscreen || el.webkitRequestFullscreen || function () {}).call(el);
    });
  }

  function ensureModal() {
    var existing = document.querySelector('.va__player');
    if (existing) {
      /* legacy ck-realtors player gets controls injected if missing */
      if (!existing.querySelector('.va__player-controls')) {
        var stage = existing.querySelector('.va__player-stage');
        if (stage) stage.insertAdjacentHTML('beforeend', controlsHTML());
      }
      return existing;
    }
    var html =
      '<div class="va__player" aria-hidden="true" role="dialog" aria-modal="true" aria-label="Video player">' +
        '<div class="va__player-stage">' +
          '<video class="va__player-video" playsinline></video>' +
          '<button class="va__player-toggle" type="button" aria-label="Pause / play"></button>' +
          '<span class="va__player-time">0:00 / 0:00</span>' +
          '<div class="va__player-bar"><span class="va__player-bar-fill"></span></div>' +
          controlsHTML() +
          '<button class="va__player-close" type="button" aria-label="Close video"></button>' +
        '</div>' +
      '</div>';
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var node = wrap.firstChild;
    document.body.appendChild(node);
    return node;
  }
  function controlsHTML() {
    return '' +
      '<div class="va__player-controls">' +
        '<div class="va__player-vol-wrap">' +
          '<button class="va__player-vol-btn" type="button" aria-label="Mute / unmute">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true">' +
              '<path d="M4 9 L8 9 L13 5 L13 19 L8 15 L4 15 Z" />' +
              '<path class="va__vol-on" d="M16 8 C18 10 18 14 16 16" />' +
              '<path class="va__vol-on" d="M18.5 6 C21.5 9 21.5 15 18.5 18" />' +
            '</svg>' +
          '</button>' +
          '<input class="va__player-vol" type="range" min="0" max="100" step="1" value="100" aria-label="Volume" />' +
        '</div>' +
        '<button class="va__player-ctl va__player-fs" type="button" aria-label="Fullscreen">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M4 9 L4 4 L9 4" />' +
            '<path d="M20 9 L20 4 L15 4" />' +
            '<path d="M4 15 L4 20 L9 20" />' +
            '<path d="M20 15 L20 20 L15 20" />' +
          '</svg>' +
        '</button>' +
      '</div>';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   COOKIE CONSENT — sitewide.
   Banner is injected on first visit (no localStorage flag yet)
   and reads from `hoa_cookie_consent`. Two actions:
     - Accept all      → flag = "all"
     - Reject optional → flag = "essential"
   Both close the banner with a transition. Future analytics /
   third-party scripts can gate themselves on:
     window.HoaCookieConsent.granted("analytics")
   so the banner is the single source of truth. ============== */
(function () {
  if (typeof document === "undefined") return;
  var KEY = "hoa_cookie_consent";

  function read() {
    try { return localStorage.getItem(KEY) || ""; } catch (e) { return ""; }
  }
  function write(value) {
    try { localStorage.setItem(KEY, value); } catch (e) { /* ignore quota */ }
  }
  function granted(category) {
    var v = read();
    if (!v) return false;
    if (v === "all") return true;
    if (v === "essential") return category === "essential";
    return false;
  }
  // Public hook so future analytics / pixel snippets can self-gate.
  window.HoaCookieConsent = { read: read, granted: granted };

  function build() {
    var el = document.createElement("aside");
    el.className = "hoa-cookie";
    el.setAttribute("role", "region");
    el.setAttribute("aria-label", "Cookie consent");
    el.innerHTML =
      '<strong class="hoa-cookie__title">Cookies, briefly.</strong>' +
      '<p class="hoa-cookie__body">We use a small set of cookies to keep the site working and, with your consent, to measure how our pages perform. Read the <a href="/privacy">privacy policy</a> for the full picture.</p>' +
      '<div class="hoa-cookie__row">' +
        '<button type="button" class="hoa-cookie__btn hoa-cookie__btn--primary" data-cookie-accept>Accept all</button>' +
        '<button type="button" class="hoa-cookie__btn" data-cookie-reject>Reject optional</button>' +
      '</div>';
    return el;
  }

  function init() {
    if (read()) return;
    var el = build();
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add("is-visible"); });

    var dismiss = function (value) {
      write(value);
      el.classList.remove("is-visible");
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 520);
      try {
        window.dispatchEvent(new CustomEvent("hoa:cookie-consent", { detail: { value: value } }));
      } catch (e) { /* IE shim not needed */ }
    };
    el.querySelector("[data-cookie-accept]").addEventListener("click", function () { dismiss("all"); });
    el.querySelector("[data-cookie-reject]").addEventListener("click", function () { dismiss("essential"); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   INLINE VIDEO PLAYER (Salt-Cinema reference).
   Wires every `.va__vp` block on the page:
     - Click play disc OR the video surface → toggles play/pause
       in place (no modal, no redirect).
     - Time display updates every requestAnimationFrame.
     - Volume button toggles mute.
     - Fullscreen button calls native requestFullscreen() on the
       player container so the chrome travels with the video.
     - Click the bottom progress bar to seek.
     - Chrome auto-hides after 2.6s of idle play, restores on
       cursor-enter or pointer-move. Pause always re-shows.
   First applied to villa-rafaella.html. Once approved we'll
   replace the modal-click pattern site-wide.
============================================================ */
(function () {
  function ready(fn) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }
  ready(function () {
    var nodes = document.querySelectorAll(".va__vp");
    if (!nodes.length) return;
    nodes.forEach(setup);
  });

  function fmt(s) {
    if (!isFinite(s) || s < 0) return "00:00";
    var m = Math.floor(s / 60), x = Math.floor(s % 60);
    return (m < 10 ? "0" : "") + m + ":" + (x < 10 ? "0" : "") + x;
  }

  /* Inject missing chrome so a tile only needs minimal markup:
        <div class="va__vp va__vp--landscape">
          <video class="va__vp__video" poster="..." playsinline loop>
            <source src="..." type="video/mp4" />
          </video>
        </div>
     The shade gradients, play disc, chrome row (time + vol slider +
     fullscreen) and the progress bar are all built on first init. =====*/
  function buildChrome(root) {
    var label = root.getAttribute("aria-label") || "Play video";
    var html = "";
    if (!root.querySelector(".va__vp__shade-top"))
      html += '<div class="va__vp__shade-top" aria-hidden="true"></div>';
    if (!root.querySelector(".va__vp__shade"))
      html += '<div class="va__vp__shade" aria-hidden="true"></div>';
    if (!root.querySelector(".va__vp__play"))
      html +=
        '<button class="va__vp__play" type="button" aria-label="' + label + '">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6 L19 12 L9 18 Z"/></svg>' +
        '</button>';
    if (!root.querySelector(".va__vp__chrome"))
      html +=
        '<div class="va__vp__chrome">' +
          '<span class="va__vp__time">00:00 / 00:00</span>' +
          '<div class="va__vp__ctrls">' +
            '<span class="va__vp__vol">' +
              '<button class="va__vp__btn" type="button" data-vp-vol aria-label="Mute / unmute">' +
                '<svg viewBox="0 0 24 24" aria-hidden="true">' +
                  '<path class="vp-fill" d="M5 9 L9 9 L13 5.5 L13 18.5 L9 15 L5 15 Z"/>' +
                  '<path class="vp-vol-on" d="M16 9 C18 11 18 13 16 15"/>' +
                  '<path class="vp-vol-on" d="M18.6 7 C21.6 10 21.6 14 18.6 17"/>' +
                  '<path class="vp-vol-slash" d="M16 9 L21 14 M21 9 L16 14" style="opacity:0;"/>' +
                '</svg>' +
              '</button>' +
              '<input class="va__vp__vol-slider" type="range" min="0" max="100" step="1" value="100" aria-label="Volume" />' +
            '</span>' +
            '<button class="va__vp__btn" type="button" data-vp-fs aria-label="Fullscreen">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true">' +
                '<path d="M4 9 L4 4 L9 4"/>' +
                '<path d="M20 9 L20 4 L15 4"/>' +
                '<path d="M4 15 L4 20 L9 20"/>' +
                '<path d="M20 15 L20 20 L15 20"/>' +
              '</svg>' +
            '</button>' +
          '</div>' +
        '</div>';
    if (!root.querySelector(".va__vp__progress"))
      html += '<div class="va__vp__progress" role="slider" aria-label="Seek"><span class="va__vp__progress-fill"></span></div>';
    if (html) root.insertAdjacentHTML("beforeend", html);
  }

  function setup(root) {
    var video = root.querySelector(".va__vp__video");
    if (!video) return;
    buildChrome(root);
    var btnPlay = root.querySelector(".va__vp__play");
    var btnVol  = root.querySelector("[data-vp-vol]");
    var volSlider = root.querySelector(".va__vp__vol-slider");
    var btnFs   = root.querySelector("[data-vp-fs]");
    var btnSet  = root.querySelector("[data-vp-set]");
    var elTime  = root.querySelector(".va__vp__time");
    var bar     = root.querySelector(".va__vp__progress");
    var fill    = root.querySelector(".va__vp__progress-fill");

    var raf = null;
    var idleTimer = null;
    /* Salt-Cinema cadence: chrome retracts almost immediately after
       play starts, restoring on hover or pause. */
    var IDLE_MS = 700;

    function tick() {
      if (!video.duration || isNaN(video.duration)) {
        elTime && (elTime.textContent = "00:00 / 00:00");
      } else {
        var ratio = video.currentTime / video.duration;
        if (fill) fill.style.width = (ratio * 100).toFixed(2) + "%";
        if (elTime) elTime.textContent = fmt(video.currentTime) + " / " + fmt(video.duration);
      }
      raf = requestAnimationFrame(tick);
    }
    function startTick() { if (!raf) raf = requestAnimationFrame(tick); }
    function stopTick()  { if (raf) { cancelAnimationFrame(raf); raf = null; } }

    function showChrome() {
      root.classList.remove("is-idle");
      clearTimeout(idleTimer);
      if (!video.paused) {
        idleTimer = setTimeout(function () {
          if (!video.paused) root.classList.add("is-idle");
        }, IDLE_MS);
      }
    }

    function tryPlay() {
      video.muted = false;
      var p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch(function () { video.muted = true; updateVolIcon(); video.play().catch(function () {}); });
      }
    }
    function tryPause() { try { video.pause(); } catch (e) {} }

    function toggle() {
      if (video.paused) tryPlay(); else tryPause();
      showChrome();
    }

    /* play button + clicking on the video surface itself toggle. We
       avoid hijacking clicks on the chrome buttons / progress bar by
       checking event.target. */
    btnPlay && btnPlay.addEventListener("click", function (e) {
      e.stopPropagation();
      toggle();
    });
    root.addEventListener("click", function (e) {
      if (e.target.closest(".va__vp__chrome") || e.target.closest(".va__vp__progress")) return;
      if (e.target === btnPlay || (btnPlay && btnPlay.contains(e.target))) return;
      toggle();
    });

    video.addEventListener("play",  function () { root.classList.add("is-playing"); startTick(); showChrome(); });
    video.addEventListener("pause", function () { root.classList.remove("is-playing"); stopTick(); root.classList.remove("is-idle"); clearTimeout(idleTimer); });
    video.addEventListener("ended", function () { root.classList.remove("is-playing"); root.classList.remove("is-idle"); });
    video.addEventListener("loadedmetadata", function () {
      if (elTime) elTime.textContent = fmt(0) + " / " + fmt(video.duration || 0);
    });

    /* idle-fade restore on pointer movement */
    root.addEventListener("pointermove", showChrome);
    root.addEventListener("pointerenter", showChrome);
    root.addEventListener("pointerleave", function () {
      if (!video.paused) {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () {
          if (!video.paused) root.classList.add("is-idle");
        }, 600);
      }
    });

    /* progress bar — click to seek */
    bar && bar.addEventListener("click", function (e) {
      e.stopPropagation();
      var rect = bar.getBoundingClientRect();
      var ratio = (e.clientX - rect.left) / rect.width;
      if (video.duration) video.currentTime = ratio * video.duration;
    });

    /* volume — speaker icon toggles mute, slider adjusts level. The
       slider expands on hover (CSS) and live-binds to video.volume. */
    function updateVolUI() {
      var muted = video.muted || video.volume === 0;
      if (btnVol) {
        btnVol.setAttribute("data-state", muted ? "muted" : "on");
        var pathOn = btnVol.querySelectorAll(".vp-vol-on");
        pathOn.forEach(function (p) { p.style.opacity = muted ? "0" : "1"; });
        var slash = btnVol.querySelector(".vp-vol-slash");
        if (slash) slash.style.opacity = muted ? "1" : "0";
      }
      if (volSlider && document.activeElement !== volSlider) {
        volSlider.value = muted ? 0 : Math.round((video.volume || 0) * 100);
      }
    }
    btnVol && btnVol.addEventListener("click", function (e) {
      e.stopPropagation();
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) video.volume = 0.6;
      updateVolUI();
      showChrome();
    });
    volSlider && volSlider.addEventListener("input", function (e) {
      e.stopPropagation();
      var v = parseFloat(volSlider.value) / 100;
      video.volume = isFinite(v) ? v : 0;
      video.muted = (v === 0);
      updateVolUI();
      showChrome();
    });
    video.addEventListener("volumechange", updateVolUI);
    /* Seed initial volume so the slider doesn't read zero on first paint. */
    if (video.volume === 0 && !video.muted) video.volume = 1;
    updateVolUI();

    /* fullscreen button — toggle browser fullscreen on the player root.
       The :fullscreen / :-webkit-full-screen pseudo-classes are flaky
       across Chrome/Safari on a generic <div>, so we also stamp an
       `is-fullscreen` class on the root via a fullscreenchange listener.
       CSS hooks off that class to letterbox the video correctly. */
    btnFs && btnFs.addEventListener("click", function (e) {
      e.stopPropagation();
      var d = document;
      var inFs = d.fullscreenElement || d.webkitFullscreenElement;
      if (inFs) {
        (d.exitFullscreen || d.webkitExitFullscreen || function () {}).call(d);
        return;
      }
      // Wave 83.58 — iOS Safari: requestFullscreen() on a <div> container
      // is a no-op even when iOS 18 reports fullscreenEnabled:true. Route
      // to <video>.webkitEnterFullscreen() which launches the native iOS
      // player (rotation + native chrome + native Done button). The
      // webkitEnterFullscreen method only exists on iOS so its presence
      // is the cleanest iOS detection.
      if (video && typeof video.webkitEnterFullscreen === "function") {
        try {
          if (video.readyState < 1) video.load();
          video.webkitEnterFullscreen();
          return;
        } catch (e2) { /* fall through to container path */ }
      }
      var rq = root.requestFullscreen || root.webkitRequestFullscreen;
      if (rq) rq.call(root);
    });
    function syncFsClass() {
      var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      root.classList.toggle("is-fullscreen", fsEl === root);
    }
    document.addEventListener("fullscreenchange", syncFsClass);
    document.addEventListener("webkitfullscreenchange", syncFsClass);

    /* settings — placeholder cycle of playback rate (1x → 1.25x →
       1.5x → 0.5x). Kept minimal so the icon does something. */
    var rates = [1, 1.25, 1.5, 0.5];
    var rateIdx = 0;
    btnSet && btnSet.addEventListener("click", function (e) {
      e.stopPropagation();
      rateIdx = (rateIdx + 1) % rates.length;
      try { video.playbackRate = rates[rateIdx]; } catch (err) {}
      btnSet.setAttribute("data-rate", rates[rateIdx] + "x");
      showChrome();
    });

    /* keyboard a11y on the play button */
    btnPlay && btnPlay.addEventListener("keydown", function (e) {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggle();
      }
    });
  }
})();

/* ============================================================
   MORE WORK lightbox.
   Click any `.projects-dump__cell[data-video-src]` and the video
   opens in a full-page glass lightbox with native HTML5 controls.
   Click outside / hit Esc / click the × to close. ============== */
(function () {
  if (typeof document === "undefined") return;
  document.addEventListener("click", function (e) {
    var cell = e.target.closest && e.target.closest(".projects-dump__cell[data-video-src]");
    if (!cell) return;
    var src = cell.getAttribute("data-video-src");
    if (!src) return;
    openLightbox(src);
  });

  function openLightbox(src) {
    var modal = document.createElement("div");
    modal.className = "pj-lightbox";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML =
      '<button class="pj-lightbox__close" type="button" aria-label="Close video"></button>' +
      '<div class="pj-lightbox__stage">' +
        '<video class="pj-lightbox__video" controls autoplay playsinline>' +
          '<source src="' + src + '" type="video/mp4" />' +
        '</video>' +
      '</div>';
    document.body.appendChild(modal);
    requestAnimationFrame(function () { modal.classList.add("is-open"); });
    document.body.classList.add("pj-lightbox-open");

    function close() {
      modal.classList.remove("is-open");
      document.body.classList.remove("pj-lightbox-open");
      var v = modal.querySelector("video");
      if (v) { try { v.pause(); } catch (e) {} }
      setTimeout(function () { if (modal.parentNode) modal.parentNode.removeChild(modal); }, 360);
      document.removeEventListener("keydown", onKey);
    }
    function onKey(e) { if (e.key === "Escape") close(); }
    document.addEventListener("keydown", onKey);
    modal.querySelector(".pj-lightbox__close").addEventListener("click", close);
    modal.addEventListener("click", function (e) { if (e.target === modal) close(); });
  }
})();

// Newsletter form: posts to Netlify Forms via fetch and surfaces success state
// in-place so the user does not bounce off-page. The form must declare
// `data-newsletter-form` and contain `[data-newsletter-status]` for the
// status line, plus a hidden `form-name` input that matches the name in
// __forms.html (currently "hoa-newsletter").
(function () {
  var forms = document.querySelectorAll("form[data-newsletter-form]");
  if (!forms.length) return;
  // Loose RFC-5322 shape check — pre-flight so a typo gets caught locally
  // and we don't waste a Netlify Forms POST on it.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Pull a UTM param out of the current URL. Returns "" if absent.
  function utm(name) {
    try {
      return new URL(window.location.href).searchParams.get(name) || "";
    } catch (e) { return ""; }
  }

  // Populate the hidden context fields once on page-ready so the JS handler
  // doesn't have to look them up on every submit. Each Netlify Forms row gets
  // source_page, referrer, signup_intent, locale, plus utm_* — enough to join
  // the signup back to the page it came from when the user later wires a CRM.
  function fillContext(form) {
    var pageUrl = window.location.href.split("#")[0];
    var fields = {
      "[data-newsletter-source]": pageUrl,
      "[data-newsletter-referrer]": document.referrer || "",
      "[data-newsletter-locale]": (document.documentElement.getAttribute("lang") || navigator.language || ""),
      "[data-newsletter-utm-source]": utm("utm_source"),
      "[data-newsletter-utm-medium]": utm("utm_medium"),
      "[data-newsletter-utm-campaign]": utm("utm_campaign")
    };
    Object.keys(fields).forEach(function (sel) {
      var el = form.querySelector(sel);
      if (el && !el.value) el.value = fields[sel];
    });
  }

  forms.forEach(function (form) {
    fillContext(form);
    // Wave 69 anti-spam — stamp the form with the load time. Server rejects
    // submissions that arrive in under 3 seconds (bot-fast).
    var tsOpen = form.querySelector("[data-newsletter-ts]");
    if (tsOpen && !tsOpen.value) tsOpen.value = String(Date.now());
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      // Re-fill in case the visitor navigated within an SPA-like flow.
      fillContext(form);
      var status = form.querySelector("[data-newsletter-status]");
      var btn = form.querySelector("button[type=submit]");
      var email = form.querySelector("input[name=email]");
      var emailValue = email ? email.value.trim() : "";
      if (!emailValue || !EMAIL_RE.test(emailValue)) {
        if (status) {
          status.textContent = "Please enter a valid email.";
          status.setAttribute("data-state", "error");
        }
        if (email) email.focus();
        return;
      }
      if (btn) btn.disabled = true;
      if (status) { status.textContent = "Subscribing."; status.removeAttribute("data-state"); }
      var data = new FormData(form);
      // Wave 67 fix — was posting to '/' (Netlify-Forms-era), now to
      // /api/newsletter so the Resend-wired Pages Function actually
      // ships the email.
      fetch("/api/newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams(data).toString()
      }).then(function (r) {
        if (!r.ok) throw new Error("Network");
        if (status) { status.textContent = "Subscribed. First dispatch incoming."; status.setAttribute("data-state", "ok"); }
        if (email) email.value = "";
      }).catch(function () {
        if (status) { status.textContent = "Could not subscribe. Please try again."; status.setAttribute("data-state", "error"); }
      }).finally(function () {
        if (btn) btn.disabled = false;
      });
    });
  });
})();

/* ============================================================
   Approach numbered-process — toggle accordion on mobile/tablet
   Below 1024px the desktop scroll-driven storytelling is
   replaced by a tappable accordion: each .av2-step__kicker
   row toggles .is-open on its parent .av2-step. Step 1 opens
   on load. CSS in main.css handles the visual collapse via
   max-height transitions.
============================================================ */
(function () {
  if (!window.matchMedia('(max-width: 1024px)').matches) return;
  var steps = document.querySelectorAll('.av2-rail .av2-step');
  if (!steps.length) return;
  steps.forEach(function (step, idx) {
    if (idx === 0) step.classList.add('is-open');
    var kicker = step.querySelector('.av2-step__kicker');
    if (!kicker) return;
    kicker.setAttribute('role', 'button');
    kicker.setAttribute('tabindex', '0');
    var toggle = function () { step.classList.toggle('is-open'); };
    kicker.addEventListener('click', toggle);
    kicker.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  });
})();


/* ============================================================
   Home process .approach--hscroll on mobile/tablet:
   convert each .hstep into a click-to-toggle accordion.
   ----------------------------------------------------------------
   Wave 17 fix: the markup has NO .hstep__phase element. The real
   structure is .hstep > .hstep__content > (.hstep__crown +
   .hstep__title + .hstep__desc + .hstep__deliverables). We wrap
   .hstep__crown + .hstep__title into a single <button> tap target
   (.hstep__header), then click toggles .is-open on the parent .hstep.
   First step opens by default. CSS in main.css (Wave 17.F) handles
   the visual collapse + chevron rotation.
============================================================ */
(function () {
  function init() {
    if (!window.matchMedia('(max-width: 1024px)').matches) return;
    var steps = document.querySelectorAll('.approach--hscroll .hstep');
    if (!steps.length) return;
    steps.forEach(function (step, idx) {
      var content = step.querySelector('.hstep__content');
      if (!content) return;
      // Skip if header already injected (e.g. on resize re-runs)
      if (content.querySelector('.hstep__header')) return;
      var crown = content.querySelector('.hstep__crown');
      var title = content.querySelector('.hstep__title');
      if (!title) return;

      var header = document.createElement('button');
      header.type = 'button';
      header.className = 'hstep__header';
      header.setAttribute('aria-expanded', idx === 0 ? 'true' : 'false');
      if (crown) header.appendChild(crown);
      header.appendChild(title);
      // Insert header at the top of .hstep__content so .hstep__desc and
      // .hstep__deliverables remain after it as the collapsible body.
      content.insertBefore(header, content.firstChild);

      if (idx === 0) step.classList.add('is-open');

      // Per-header listener intentionally NOT bound — the delegated
      // capture-phase listener below is the single source of truth.
      // Avoids double-toggle when both fire on the same click.
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Single delegated handler — bulletproof. Catches any click that
     lands on a .hstep header / title / crown on a mobile viewport
     and toggles the parent .hstep. stopPropagation prevents any
     deeper handlers from firing twice. */
  document.addEventListener('click', function (e) {
    if (!window.matchMedia('(max-width: 1024px)').matches) return;
    var hit = e.target.closest && e.target.closest('.approach--hscroll .hstep');
    if (!hit) return;
    var inHeader = e.target.closest('.hstep__header, .hstep__title, .hstep__crown');
    if (!inHeader) return;
    e.stopPropagation();
    hit.classList.toggle('is-open');
    var hdr = hit.querySelector('.hstep__header');
    if (hdr) hdr.setAttribute('aria-expanded', hit.classList.contains('is-open') ? 'true' : 'false');
  }, true); // capture phase
})();


/* ============================================================
   Homepage case-study cards — whole-card click navigation.
   ----------------------------------------------------------------
   The .case-card markup has two anchors (the .case-card__media
   wrap around the image, and the .case-card__readmore at the
   bottom). The body of the card (title, sub, stats, kicker)
   sits in a non-anchor <div>. Several CSS attempts at
   stretched links lost specificity battles with pre-existing
   rules; this delegated JS handler makes the whole card route
   to the case-study URL regardless of which child was clicked.
   No HTML edit, no CSS specificity battle.
============================================================ */
(function () {
  document.addEventListener('click', function (e) {
    var card = e.target.closest && e.target.closest('.case-card');
    if (!card) return;
    // If the user actually clicked on a real <a> inside the card
    // (the media anchor or the readmore anchor), let the native
    // navigation happen — do not double-fire.
    var clickedAnchor = e.target.closest('a[href]');
    if (clickedAnchor && card.contains(clickedAnchor)) return;
    // Find the readmore href (or fall back to the media anchor).
    var target = card.querySelector('.case-card__readmore[href]')
              || card.querySelector('.case-card__media[href]')
              || card.querySelector('a[href]');
    if (!target) return;
    var href = target.getAttribute('href');
    if (!href) return;
    // Modifier-key + middle-click → new tab. Otherwise navigate.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      window.open(href, '_blank');
    } else {
      window.location.href = href;
    }
  });
})();


/* ============================================================
   Wave 16.J — Legal pages: inject floating brand+hamburger
   ----------------------------------------------------------------
   Privacy & Terms pages bypass the site nav. They still need a
   visible brand wordmark + hamburger so the user can return to
   the homepage. We detect a `.legal-page` container and inject
   a minimal floating nav at the top. Hamburger links to "/"
   because there is no side-menu markup on these pages.
============================================================ */
(function () {
  var legalRoot = document.querySelector('.legal-page');
  if (!legalRoot) return;
  // Add a body class so 16.F CSS rules can target it.
  document.body.classList.add('legal-page-body');
  // If a floating nav was already injected (cached pages), skip.
  if (document.querySelector('.legal-floating-nav')) return;
  var nav = document.createElement('div');
  nav.className = 'legal-floating-nav';
  nav.innerHTML = [
    '<a class="legal-floating-nav__brand" href="/" aria-label="House of Advanced, return to homepage">',
    '  House <em>of</em> Advanced',
    '</a>',
    '<a class="legal-floating-nav__menu" href="/" aria-label="Return to homepage">',
    '  <svg viewBox="0 0 24 24" aria-hidden="true">',
    '    <path d="M3 7h18M3 12h18M3 17h18" stroke-linecap="square" />',
    '  </svg>',
    '</a>'
  ].join('');
  document.body.insertBefore(nav, document.body.firstChild);
})();

/* ============================================================
   Wave 18 — Home process .hstep__phase: inject step number
   as data-step-num attribute so CSS ::before can render it
   inline before the phase text on mobile/tablet.
============================================================ */
(function () {
  document.querySelectorAll('.approach--hscroll .hstep').forEach(function (step, i) {
    var phase = step.querySelector('.hstep__phase');
    if (phase && !phase.hasAttribute('data-step-num')) {
      phase.setAttribute('data-step-num', String(i + 1).padStart(2, '0'));
    }
  });
})();

/* ============================================================
   Wave 18 — proj-transform-showcase scroll-locked crossfade
   Smoothly cross-fades the night layer over the day layer
   based on the section's scroll position relative to viewport.
   xfade = 0 when the showcase is below the fold, 1 when it
   has fully scrolled past the top of viewport.
============================================================ */
(function () {
  var frame = document.querySelector('.proj-transform-showcase__frame');
  if (!frame) return;
  var ticking = false;
  function update() {
    ticking = false;
    var rect = frame.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    // start crossfade when showcase top is at 80% of viewport,
    // complete when its top is at 20%
    var raw = (vh * 0.8 - rect.top) / (vh * 0.6);
    var t = Math.max(0, Math.min(1, raw));
    frame.style.setProperty('--xfade', t.toFixed(3));
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
})();

/* ============================================================
   Wave 19 — Home process accordion: real .hstep__title toggle
   Inject data-step-num on each .hstep__title (CSS ::before
   renders it inline). Click on title toggles .is-open on
   parent .hstep. First step opens by default at <=1024px.
============================================================ */
(function () {
  if (!window.matchMedia('(max-width: 1024px)').matches) return;
  var steps = document.querySelectorAll('.approach--hscroll .hstep');
  if (!steps.length) return;
  steps.forEach(function (step, i) {
    var title = step.querySelector('.hstep__title');
    if (!title) return;
    if (!title.hasAttribute('data-step-num')) {
      title.setAttribute('data-step-num', String(i + 1).padStart(2, '0'));
    }
    if (i === 0) step.classList.add('is-open');
    title.setAttribute('role', 'button');
    title.setAttribute('tabindex', '0');
    var toggle = function () { step.classList.toggle('is-open'); };
    title.addEventListener('click', toggle);
    title.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  });
})();
